import { V1OffsetPagination } from '@bukalapak/openapigen/products/mitrabangunan/transaction/v1/public'
import {
  CategoryNode,
  GetCategoryResponse,
} from '@bukalapak/protocgen/src/products/mitrabangunan/product/v2/private/category_pb'
import type { Context } from '@nuxt/types'
import { toLower, upperFirst } from 'lodash'
import { MetaInfo } from 'vue-meta'
import { PublicCategoryApi } from '~/api/product/category'
import { SeoPropertiesAPI } from '~/api/seo-properties'
import type { CategoryProduct } from '~/types/component/landing-product'
import { MappedPaginationType } from '~/types/component/mapped-pagination-type'
import { SeoContents } from '~/types/model/seo'
import saveErrorLog from '~/utils/runner/save-error-log'

const defaultLimit = 30

const mapPagination = (pagination: V1OffsetPagination): MappedPaginationType => {
  const offset = pagination?.offset || 0
  const limit = pagination?.limit || defaultLimit
  const total = pagination?.total || 0

  return {
    currentPage: Math.ceil(offset / limit) + 1,
    limit,
    offset,
    total,
    totalPages: Math.ceil(total / limit),
  }
}

export async function fetchCategoryData(ctx: Context, category: string, pageKey: string) {
  let categoryResponse: GetCategoryResponse.AsObject = {}
  let categoryData: CategoryNode.AsObject = {
    subcategoriesList: [],
  }
  let products: CategoryProduct[] = []
  let pagination: MappedPaginationType = {
    currentPage: 0,
    limit: 0,
    offset: 0,
    total: 0,
    totalPages: 0,
  }
  let contents: SeoContents = {
    meta: {
      title: '',
      description: '',
    },
    title: '',
    list: [],
    pageDescripiton: '',
  }
  const categorySlug = category ? category.replace(/[^\d&A-z-]/gi, '') : ''
  const { page } = ctx.query
  const offset = ((Number.parseInt(`${page}`, 10) || 1) - 1) * defaultLimit
  const categoryParams = {
    slug: categorySlug,
  }
  if (process.server) {
    try {
      const { PrivateCategoryClient } = await import('~/server/api/clients/private-category')
      const categoryData = await PrivateCategoryClient.getCategory(categoryParams)
      const data = categoryData?.toObject && categoryData.toObject()
      categoryResponse = data
    } catch (error) {
      await saveErrorLog(error)
    }
  } else {
    try {
      const data = await PublicCategoryApi.getCategory(ctx.app.$axios, categoryParams)
      categoryResponse = data
    } catch (_error) {}
  }
  if (categoryResponse?.category?.category && categoryResponse?.category?.subcategoriesList) {
    categoryData = {
      category: categoryResponse?.category?.category,
      subcategoriesList: categoryResponse?.category?.subcategoriesList,
    }
    const params = {
      keyword: toLower(categoryData?.category?.name || categorySlug).replace(/-/gi, ' '),
      offset,
      limit: defaultLimit,
    }
    if (process.server) {
      try {
        const { sellingProductClient } = await import('~/server/api/clients/selling-product')
        const rpcData = await sellingProductClient.getList(params)
        const data = rpcData?.toObject && rpcData.toObject()
        if (data && data.sellingProductsList?.length > 0 && data.meta?.offsetPagination) {
          products = data.sellingProductsList.map(product => ({
            name: product.name,
            image: product.imageUrlsList?.length ? product.imageUrlsList[0] : '',
          }))
          pagination = mapPagination(data.meta?.offsetPagination)
        }
      } catch (error) {
        await saveErrorLog(error)
      }
    } else {
      try {
        const { data } = await ctx.app.$api.sellingProductApi.sellingProductServiceListSellingProduct(params)
        if (data && data.selling_products?.length && data.meta?.offset_pagination) {
          products = data.selling_products!.map(product => ({
            name: product.name || '',
            image: product.image_urls?.length ? product.image_urls[0] : '',
          }))
          pagination = mapPagination(data.meta?.offset_pagination!)
        }
      } catch (_error) {}
    }
  }
  try {
    const { seoContents } = await import(`~/constants/category/seo/${categorySlug}`)
    contents = seoContents
  } catch (_error) {}
  try {
    if (pageKey) {
      const {
        data: { data },
      } = await SeoPropertiesAPI(ctx).retrieveSEOProperties(pageKey)
      const result = data
      if (result) {
        contents = {
          ...contents,
          meta: {
            title: result.meta_title,
            description: result.meta_description,
          },
          ...(result.page_title ? { title: result.page_title } : {}),
          ...(result.page_description ? { pageDescripiton: result.page_description, list: [] } : {}),
        }
      }
    }
  } catch (_error) {}

  return {
    category: categoryData,
    products,
    pagination,
    contents,
    title: upperFirst(toLower((categoryData?.category?.name || categorySlug).replace(/-/gi, ' '))),
    categorySlug,
  }
}

export function generateHead(url: string, title: string, description: string): MetaInfo {
  return {
    title,
    meta: [
      { hid: 'og:url', property: 'og:url', content: url },
      { hid: 'description', name: 'description', content: description },
      { hid: 'og:title', property: 'og:title', content: title },
      { hid: 'og:description', property: 'og:description', content: description },
      { hid: 'og:site_name', property: 'og:site_name', content: `${title} | BukaBangunan` },
      { hid: 'twitter:title', property: 'twitter:title', content: title },
      { hid: 'twitter:description', property: 'twitter:description', content: description },
    ],
    link: [{ rel: 'canonical', href: url }],
  }
}
